import React, {
  FC, useState, useCallback, MouseEvent,
} from 'react';
import ReactPaginate from 'react-paginate';
import { observer } from 'mobx-react';

import { useTranslation } from 'next-i18next';
import useAfterRenderCallback from 'src/common/hooks/useAfterRenderCallback';
import PaginationArrow from './PaginationArrow';

import useMediaQueryList from '../../hooks/useMediaQueryList';
import getClassNames from '../../utils/getClassNames';
import getTotalListings from '../../utils/getTotalListings';
import getPageCount from '../../utils/getPageCount';

import styles from './Pagination.module.scss';

const ABOVE_550_PX: string = 'only screen and (min-width: 551px)';
const BELOW_550_PX: string = 'only screen and (max-width: 550px)';

/**
 * TODO: react-paginate library is showing issues displaying pages and break according to
 * the pageRangeDisplayed and marginPagesDisplayed config in mobile view. It seems to be
 * a library issue but needs to investigate properly.
 */

const defaultConfig = {
  [ABOVE_550_PX]: {
    marginPagesDisplayed: 1,
    pageRangeDisplayed: 3,
  },
  [BELOW_550_PX]: {
    marginPagesDisplayed: 1,
    pageRangeDisplayed: 1,
  },
};

export type PaginationProps = {
  classNameModifier?: string;
  /**
   * The total number of items
   */
  count: number;
  /**
   * The number of items per page
   */
  limit: number;
  /**
   * The page number
   */
  page: number;
  /**
   * Callback function for when the page changes
   */
  onChange: (page: number) => void;
  /**
   * Optional function for tracking pagination clicks
   */
  trackPaginationClick?: (page: number) => void;
  /**
   * Function used to construct the pagination hrefs
   */
  hrefBuilder: (page: number) => string;
  /**
   * Callback function for intercepting `<a/>` element click
   */
  onLinkClick?: (clickEvent: {
    event: MouseEvent<HTMLAnchorElement>;
    nextSelectedPage: number | undefined;
  }) => void;
  /**
   * Optionally display total results below pagination
   */
  withTotalCount?: boolean;
};

const Pagination = observer<FC<PaginationProps>>(
  ({
    classNameModifier,
    count,
    limit,
    page,
    onChange,
    onLinkClick = ({ event }) => event.preventDefault(),
    trackPaginationClick,
    hrefBuilder,
    withTotalCount = true,
  }) => {
    const [t] = useTranslation('common');
    const [config, setConfig] = useState(defaultConfig[BELOW_550_PX]);

    const handleResize = ({ media, matches }) => {
      if (matches) {
        setConfig(defaultConfig[media] || defaultConfig[BELOW_550_PX]);
      }
    };

    useMediaQueryList(Object.keys(defaultConfig), handleResize);

    const onClick = useAfterRenderCallback(({ selected }: { selected: number }) => {
      trackPaginationClick?.(selected + 1);
      onChange(selected + 1);
    });

    const ariaLabelBuilder = useCallback(
      (p: number) => t('pagination--page-aria-label', { page: p }),
      [t],
    );
    const total = getTotalListings(count, true);
    const pageCount = getPageCount(count, limit);
    const forcePage = (page || 1) - 1;

    return (
      <nav className={getClassNames(styles, 'root', classNameModifier)} aria-label="Pagination">
        <ReactPaginate
          previousAriaLabel={t('pagination--previous-aria-label')}
          previousLabel={<PaginationArrow variant="previous" />}
          previousClassName={getClassNames(styles, 'buttonWrapper', 'prev')}
          previousLinkClassName={getClassNames(styles, 'button', 'prev')}
          nextAriaLabel={t('pagination--next-aria-label')}
          nextLabel={<PaginationArrow variant="next" />}
          nextClassName={total === '0'
            ? getClassNames(styles, 'buttonWrapper', 'next disabled')
            : getClassNames(styles, 'buttonWrapper', 'next')}
          nextLinkClassName={getClassNames(styles, 'button', 'next')}
          disabledClassName={styles.buttonWrapper_disabled}
          breakLabel="..."
          breakClassName={styles.break}
          pageCount={pageCount}
          forcePage={forcePage}
          pageRangeDisplayed={config.pageRangeDisplayed}
          marginPagesDisplayed={config.marginPagesDisplayed}
          pageClassName={styles.item}
          pageLinkClassName={styles.page}
          activeClassName={styles.item_current}
          onPageChange={onClick}
          containerClassName={styles.list}
          hrefBuilder={hrefBuilder}
          ariaLabelBuilder={ariaLabelBuilder}
          // @ts-expect-error -  onClick is incorrect typed by react-paginate
          onClick={onLinkClick}
          selectedPageRel={null}
        />
        {withTotalCount && (
          <h5 className={styles.label}>{t('pagination--total-results', { total })}</h5>
        )}
      </nav>
    );
  },
);

export default Pagination;
